import { useEffect, useMemo } from 'react'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Outlet, useLocation, useNavigate, useParams } from 'react-router'
import { Box, CssBaseline, Fade, LinearProgress } from '@mui/material'
import { useAuth } from '~/api/auth'
import { ConfirmProvider, Navigation, RenderCount } from '~/components'
import { bshSlice } from '~/store'
import ThemeProvider from '~/theme/ThemeProvider'
import actions from '../actions'
import models from '../models'
import RouteContext from './RouteContext'

const useRouteGuard = () => {
  const { isAuth } = useAuth()
  const location = useLocation()
  const params = useParams()
  const navigate = useNavigate()
  const brands = useSelector(bshSlice.brands)

  const brand = useMemo(() => {
    return brands?.find((brand) => brand.url === params.brand_path)
  }, [brands, params.brand_path])

  const defaultBrand = useMemo(() => {
    return Array.isArray(brands) && brands.length ? brands[0] : undefined
  }, [brands])

  const action = useMemo(() => {
    const action = location.pathname.split('/')[2]
    return action ? actions.find(({ path }) => path === action) : undefined
  }, [location.pathname])

  const model = useMemo(() => {
    const model = location.pathname.split('/')[3]
    return models.includes(model) ? model : undefined
  }, [location.pathname])

  const id = useMemo(() => {
    return params.id ? parseInt(params.id, 10) : undefined
  }, [params.id])

  const pathObject = useMemo(
    () => ({
      brand_url: brand?.url,
      model,
      id,
    }),
    [brand?.url, id, model]
  )

  const treePath = useSelector((state) => bshSlice.treePath(state, pathObject))

  const categoryId = useMemo(() => {
    return treePath?.length >= 2 ? treePath[1].id : undefined
  }, [treePath])

  const category = useSelector((state) =>
    bshSlice.categoryById(state, categoryId)
  )

  const subCategoryId = useMemo(() => {
    return treePath?.length >= 3 ? treePath[2].id : undefined
  }, [treePath])

  const subCategory = useSelector((state) =>
    bshSlice.categoryById(state, subCategoryId)
  )

  const productId = useMemo(() => {
    return treePath?.length >= 4 ? treePath[3].id : undefined
  }, [treePath])

  const product = useSelector((state) => bshSlice.productById(state, productId))

  useEffect(() => {
    if (isAuth && !brand && defaultBrand) {
      navigate('/')
      return
    }
  }, [action, brand, defaultBrand, isAuth, navigate])

  useEffect(() => {
    if (params.model && !model) {
      if (!brand) return
      navigate(`/${brand.url}/${action.path}`)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model, params.model])

  return useMemo(
    () => ({
      brand: brand ?? defaultBrand,
      action,
      model,
      id,
      treePath,
      category,
      location,
      subCategory,
      product,
    }),
    [
      brand,
      defaultBrand,
      action,
      model,
      id,
      treePath,
      category,
      location,
      subCategory,
      product,
    ]
  )
}

const BSHApp = () => {
  const { isAuth } = useAuth()
  const { t } = useTranslation()
  const route = useRouteGuard()
  const dispatch = useDispatch()
  const ready = useSelector(bshSlice.ready)
  const isPendingRequest = useSelector(bshSlice.isPendingRequest)

  useEffect(() => {
    if (!isAuth) return
    const intId = setTimeout(() => {
      if (!ready) {
        dispatch(bshSlice.getTree())
      }
    }, 10)
    return () => clearTimeout(intId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuth])

  useEffect(() => {
    const brandUrl = route.brand?.url
    if (brandUrl) {
      dispatch(bshSlice.getEmpCo(brandUrl))
    }
  }, [dispatch, route.brand?.url])

  const progress = useMemo(
    () => (
      <Fade
        in={isPendingRequest}
        style={{
          transitionDelay: isPendingRequest ? '500ms' : '0ms',
        }}
        unmountOnExit
      >
        <Box sx={{ position: 'relative' }}>
          {isPendingRequest && (
            <LinearProgress
              sx={{ position: 'absolute', width: '100%', zIndex: 999 }}
            />
          )}
        </Box>
      </Fade>
    ),
    [isPendingRequest]
  )

  return useMemo(
    () => (
      <RouteContext.Provider value={route}>
        <ThemeProvider>
          <Helmet
            defaultTitle={t('site.title')}
            titleTemplate={`%s - ${t('site.title')}`}
          />
          <CssBaseline />
          <ConfirmProvider>
            <Navigation />
            {progress}
            <DndProvider backend={HTML5Backend}>
              <RenderCount hide />
              <div className="grow grid">
                <Outlet />
              </div>
            </DndProvider>
          </ConfirmProvider>
        </ThemeProvider>
      </RouteContext.Provider>
    ),
    [progress, route, t]
  )
}

export default BSHApp
